import React from 'react';

import { ACCOUNT_CONTINUITY_HINT } from './accountModel';
import { AccountPageShell } from './AccountPageShell';
import {
  AccountCard,
  Grid,
  NotYetButton,
  Row,
  RowList,
  StatusChip,
} from './primitives';

/**
 * AR-380 — Documents. Shows to non-owners as well: a member sees the page when
 * they have a document, and every member has at least one, so it is not empty
 * for them.
 *
 * Agreements and credentials moved to Membership with their cards, so the strip
 * names what this page actually holds.
 */

/** The structured records the library will keep, and the fields each holds. */
const LIBRARY_RECORDS: readonly {
  title: string;
  multi?: boolean;
  fields: readonly string[];
}[] = [
  {
    title: 'E&O insurance',
    fields: [
      'Insurance carrier',
      'Policy number',
      'Effective date',
      'Expiration date',
      'Certificate',
    ],
  },
  {
    title: 'Seller of Travel registration',
    multi: true,
    fields: [
      'State or jurisdiction',
      'Registration number',
      'Effective date',
      'Expiration date',
      'Supporting document',
    ],
  },
  {
    title: 'Business registration',
    multi: true,
    fields: [
      'Registration type',
      'State or jurisdiction',
      'Registration number',
      'Filing date',
      'Expiration date',
      'Supporting document',
    ],
  },
];

const LIBRARY_CATEGORIES = [
  'Insurance',
  'Registrations and licenses',
  'Tax',
  'Supplier documents',
  'Contracts',
  'Business operations',
  'Marketing',
  'Other',
];

const Tile: React.FC<{ name: string; status: string }> = ({ name, status }) => (
  <div className="rounded-lg border border-gray-200 bg-white px-3.5 py-3 dark:border-gray-700 dark:bg-[#1A2021]">
    <span className="block text-[11.5px] font-semibold uppercase tracking-[0.14em] text-orange-700 dark:text-orange-400">
      {name}
    </span>
    <span className="mt-1.5 block">
      <StatusChip status={status} />
    </span>
  </div>
);

export const AccountDocuments: React.FC = () => (
  <AccountPageShell page="documents">
    <Grid cols={3}>
      <Tile name="Tax information" status="Complete" />
      <Tile name="Business continuity" status="Action required" />
      <Tile name="Document library" status="Not kept yet" />
    </Grid>

    <Grid cols={2} className="mt-3.5">
      <AccountCard name="Business continuity">
        <Row
          label={
            <>
              Designation status{' '}
              <span
                title={ACCOUNT_CONTINUITY_HINT}
                aria-label={ACCOUNT_CONTINUITY_HINT}
                role="note"
                tabIndex={0}
                className="ml-1 inline-flex h-4 w-4 cursor-help items-center justify-center rounded-full bg-gray-100 text-[10px] font-bold text-gray-500 dark:bg-gray-800 dark:text-gray-400"
              >
                ?
              </span>
            </>
          }
          chip={<StatusChip status="Not on file" />}
        />
        <Row label="Designated contact" value="—" />
        <Row label="Last updated" value="—" last />
        <div className="mt-2.5">
          <NotYetButton note="Upload lands with the file store — Member Success files it for you today">
            Add or update contact
          </NotYetButton>
        </div>
      </AccountCard>

      <AccountCard name="Tax information">
        <Row
          label="W-9"
          value="Last updated Jan '26"
          chip={<StatusChip status="On file" />}
          control={
            <NotYetButton note="Member Success swaps a W-9 for you while document upload moves into the platform">
              Update W-9
            </NotYetButton>
          }
        />
        <Row label="EIN" value="···· 6621" chip={<StatusChip status="On file" />} last />
      </AccountCard>
    </Grid>

    <div className="mt-3.5 rounded-lg border border-gray-200 bg-white px-4 py-3.5 dark:border-gray-700 dark:bg-[#1A2021]">
      <div className="mb-3">
        <span className="text-[13px] font-semibold uppercase tracking-[0.14em] text-orange-700 dark:text-orange-400">
          Business document library
        </span>
      </div>
      <p className="-mt-1 mb-2 text-[14px] text-gray-500 dark:text-gray-400">
        Documents you keep for your own business. Nothing here is required by WorldVia.
        Records with an expiration date will remind you before they lapse.
      </p>
      <Grid cols={3}>
        {LIBRARY_RECORDS.map((r) => (
          <div
            key={r.title}
            className="rounded-lg border border-gray-200 px-3.5 py-3 dark:border-gray-700"
          >
            <div className="mb-3 flex items-baseline justify-between gap-3">
              <span className="text-[13px] font-semibold uppercase tracking-[0.14em] text-orange-700 dark:text-orange-400">
                {r.title}
              </span>
              <StatusChip status="Not kept yet" />
            </div>
            {r.fields.map((f, i) => (
              <Row key={f} label={f} value="—" last={i === r.fields.length - 1} />
            ))}
            {r.multi ? (
              <p className="mt-2 text-[13px] text-gray-500 dark:text-gray-400">
                One record per registration.
              </p>
            ) : null}
          </div>
        ))}
      </Grid>
      <RowList label="Categories" items={LIBRARY_CATEGORIES} last />
      <div className="mt-2">
        <NotYetButton note="The document library lands with the file store">
          Upload document
        </NotYetButton>
      </div>
    </div>
  </AccountPageShell>
);

export default AccountDocuments;
